import React, { useCallback, useEffect, useState } from "react";
import { CalendarClock, ChevronDown, ChevronUp, FileUp, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { apiClient } from "@/lib/apiClient";
import { formatDate, formatDateTime } from "@/lib/formatDate";

const KIND_LABEL = { csv: "CSV", camt053: "camt.053", "camt053-ki": "camt.053 · KI-Import" };
const TONE = {
  green: "border-emerald-200 bg-emerald-50 text-emerald-900",
  amber: "border-amber-300 bg-amber-50 text-amber-900",
  red: "border-rose-300 bg-rose-50 text-rose-900",
  grey: "border-slate-200 bg-slate-50 text-slate-700",
};

function fmtIban(i) {
  return i && i !== "—" ? i.replace(/(.{4})/g, "$1 ").trim() : "Hauptkonto";
}

/** Bank-Abgleich: Wann war der letzte Bankauszug-Upload, bis wann liegen Bewegungen vor, ab wann fehlen sie? */
export function BankImportStatusCard({ companyId, refreshKey }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    try { setData((await apiClient.get(`/companies/${companyId}/bank/import-status`)).data); } catch { setData(null); }
  }, [companyId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (!data) return null;
  const li = data.last_import;
  const tone = TONE[data.status] || TONE.grey;
  const Icon = data.status === "green" ? CheckCircle2 : AlertTriangle;
  const multi = (data.accounts || []).length > 1;

  return (
    <Card className={`p-3 border ${tone}`} data-testid="bank-import-status-card">
      <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
        <div className="flex items-start gap-2 min-w-[16rem]">
          <FileUp className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="text-xs">
            <div className="font-semibold">Letzter Bankauszug-Upload</div>
            {li ? (
              <div data-testid="bank-last-import">
                <span className="font-medium">{formatDateTime(li.uploaded_at)}</span>
                {" · "}{li.file_count > 1 ? `${li.file_count} Dateien` : (li.files?.[0] || "1 Datei")}
                {" · "}{KIND_LABEL[li.kind] || li.kind}
                {" · "}{li.transaction_count} Bewegungen verbucht{li.skipped_duplicates ? ` (${li.skipped_duplicates} Doubletten)` : ""}
                {li.date_from && li.date_to && <> · Auszug {formatDate(li.date_from)} – {formatDate(li.date_to)}</>}
                {li.status === "failed" && <span className="ml-1 text-rose-700 font-medium">fehlgeschlagen{li.error ? `: ${li.error}` : ""}</span>}
              </div>
            ) : <div className="opacity-80" data-testid="bank-no-import">Noch kein Bankauszug hochgeladen.</div>}
          </div>
        </div>
        <div className="flex items-start gap-2">
          <Icon className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="text-xs" data-testid="bank-missing-from">
            <div className="font-semibold">Bankbewegungen vorhanden bis</div>
            {data.last_transaction_date ? (
              <div>
                <span className="font-medium">{formatDate(data.last_transaction_date)}</span>
                {" → "}
                <span className="font-semibold">fehlen ab {formatDate(data.missing_from)}</span>
                {data.days_missing != null && <span> ({data.days_missing === 0 ? "aktuell" : `${data.days_missing} Tage bis heute, ${formatDate(data.today)}`})</span>}
              </div>
            ) : <div className="opacity-80">Noch keine Bankbewegungen verbucht.</div>}
            {multi && (
              <ul className="mt-1 space-y-0.5" data-testid="bank-accounts-status">
                {data.accounts.map((a) => (
                  <li key={a.iban} className="flex items-center gap-1.5">
                    <span className={`inline-block h-2 w-2 rounded-full ${a.status === "green" ? "bg-emerald-500" : a.status === "amber" ? "bg-amber-500" : a.status === "red" ? "bg-rose-500" : "bg-slate-400"}`} />
                    <span className="font-mono">{fmtIban(a.iban)}</span>: bis {formatDate(a.last_date)} · fehlt ab {formatDate(a.missing_from)} · {a.count} Bewegungen
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        {(data.imports || []).length > 0 && (
          <button type="button" onClick={() => setOpen((v) => !v)} className="ml-auto inline-flex items-center gap-1 text-xs underline underline-offset-2 opacity-80 hover:opacity-100" data-testid="bank-import-history-toggle">
            <CalendarClock className="h-3.5 w-3.5" /> Upload-Verlauf ({data.imports.length}) {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>
      {open && (
        <div className="mt-2 overflow-x-auto rounded border border-white/60 bg-white/70" data-testid="bank-import-history">
          <table className="w-full text-[11px] text-slate-800">
            <thead className="text-left text-slate-500">
              <tr><th className="px-2 py-1">Hochgeladen</th><th className="px-2 py-1">Datei(en)</th><th className="px-2 py-1">Art</th><th className="px-2 py-1 text-right">Verbucht</th><th className="px-2 py-1">Auszug-Zeitraum</th><th className="px-2 py-1">Konto</th></tr>
            </thead>
            <tbody>
              {data.imports.map((f, i) => (
                <tr key={i} className="border-t border-slate-100" data-testid={`bank-import-row-${i}`}>
                  <td className="px-2 py-1 whitespace-nowrap">{formatDateTime(f.uploaded_at)}</td>
                  <td className="px-2 py-1 max-w-[22rem] truncate" title={(f.files || []).join("\n")}>{f.file_count > 1 ? `${f.file_count} Dateien (${f.files[0]} …)` : f.files?.[0] || "–"}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{KIND_LABEL[f.kind] || f.kind}{f.status === "failed" ? " · fehlgeschlagen" : f.status === "running" ? " · läuft" : ""}</td>
                  <td className="px-2 py-1 text-right whitespace-nowrap">{f.transaction_count}{f.total_in_file != null && f.total_in_file !== f.transaction_count ? ` / ${f.total_in_file}` : ""}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{f.date_from && f.date_to ? `${formatDate(f.date_from)} – ${formatDate(f.date_to)}` : "–"}</td>
                  <td className="px-2 py-1 font-mono whitespace-nowrap">{f.ibans?.length > 1 ? `${f.ibans.length} Konten` : fmtIban(f.statement_iban || f.ibans?.[0] || "—")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
